/**
 * Mutable in-memory synthetic world, plus the pure display helpers
 * (`tierHeld`, `triage`, `bandTiers`, `EVENT_LABELS`, …) that both this world
 * and the real backend's numbers are rendered through.
 *
 * `api.ts` now calls the generated SDK rather than this world — the world
 * itself is unused on the data path but kept around (and still fully
 * type-safe) in case a future screen wants an offline/demo fallback. The
 * pure helpers are the actively-used export surface; see `index.ts`.
 *
 * Numbers, states and copy are ported from the approved mockup
 * ("Vision Proactive Comms - Full App (Mockup).html") so what it shows
 * matches what was signed off. Fully deterministic — no randomness at module
 * load; `store.reset()` rebuilds the pristine world.
 */

import type {
  AttentionLevel,
  CommsState,
  EmailRecord,
  EventType,
  NotificationPreferences,
  ShareMix,
  ShareRange,
  ShareStatus,
  ShareSummaryKpis,
  TierRef,
} from "./types";

// ── Contract constants ───────────────────────────────────────────────────

/** The six lifecycle steps in stepper order (backorder is the exception lane). */
export const STEP_EVENT_TYPES: readonly EventType[] = [
  "order_received",
  "shipment_created",
  "fulfilment_status",
  "shipped",
  "passed_to_carrier",
  "delivered",
];

export const EVENT_TYPES: readonly EventType[] = [...STEP_EVENT_TYPES, "backorder"];

export const EVENT_LABELS: Record<EventType, string> = {
  order_received: "Order received",
  shipment_created: "Shipment created",
  fulfilment_status: "Fulfilment status",
  shipped: "Shipped",
  passed_to_carrier: "Passed to carrier",
  delivered: "Delivered",
  backorder: "Backorder",
};

/**
 * Programme tiers in the client's own naming (Martin, Slack 2026-07-29):
 * **T1 is the entry tier at 30%, T3 the top one at 75%.** Ascending order is
 * load-bearing — `tierHeld`/`nextTier` walk this list.
 */
export const TIERS: readonly TierRef[] = [
  { tier: "T1", value: 30 },
  { tier: "T2", value: 50 },
  { tier: "T3", value: 75 },
];

export const TOP_TIER: TierRef = TIERS[TIERS.length - 1]!;

/**
 * Silence window above the top tier: "don't send if 5% or more above 75%, so
 * 80% or more". The approval band is therefore [75, 80).
 */
export const SILENCE_MARGIN = 5;

export const QUARTERS: readonly string[] = [
  "2024Q1",
  "2024Q2",
  "2024Q3",
  "2024Q4",
  "2025Q1",
  "2025Q2",
  "2025Q3",
  "2025Q4",
  "2026Q1",
  "2026Q2",
  "2026Q3",
];

export const CURRENT_QUARTER = "2026Q3";

export const SENDER = "J&J Vision Ordering Updates <updates@jnjvision.com>";

/** Suppression reason for a whole-account opt-out (vs. a single event toggle). */
export const OPT_OUT_REASON = "Customer has opted out of proactive order emails";

/** Suppression reason for key/EDI accounts — outranks any preference. */
export const EDI_EXCLUDED_REASON = "Key/EDI account — excluded from proactive comms";

// ── Tier helpers (client rule, Martin 2026-07-29) ────────────────────────

/**
 * The four share bands the client mapped. Only the **top** tier has an
 * approval window and a silence window; everything below 75% auto-sends.
 *
 * - `below_entry` (<30%)   → auto, copy names T1 only
 * - `between`   (30–74%)   → auto, copy names the tier held **and** the next one
 * - `top_hold`  (75–79%)   → draft held for manual approval
 * - `silent`    (≥80%)     → no comms at all
 */
export type ShareBand = "below_entry" | "between" | "top_hold" | "silent";

export function classifyBand(share: number): ShareBand {
  if (share >= TOP_TIER.value + SILENCE_MARGIN) {
    return "silent";
  }
  if (share >= TOP_TIER.value) {
    return "top_hold";
  }
  if (share < TIERS[0]!.value) {
    return "below_entry";
  }
  return "between";
}

/** Highest tier the share actually meets — null below the entry tier. */
export function tierHeld(share: number): TierRef | null {
  let held: TierRef | null = null;
  for (const t of TIERS) {
    if (share >= t.value) {
      held = t;
    }
  }
  return held;
}

/** The next tier up — null once the account is at or above the top tier. */
export function nextTier(share: number): TierRef | null {
  return TIERS.find((t) => share < t.value) ?? null;
}

/**
 * Tier the displayed gap is measured against: the next tier up while one
 * exists, otherwise the top tier (so 80% reads "+5% over T3").
 */
export function targetTier(share: number): TierRef {
  return nextTier(share) ?? TOP_TIER;
}

/**
 * The tiers bounding an account's band — what the trend chart draws as dashed
 * reference lines. Takes the two API-derived fields as they arrive: the held
 * tier is null below T1, and both collapse to T3 once the top tier is held, so
 * the band has a single bound at either end of the ladder.
 */
export function bandTiers(held: TierRef | null, target: TierRef | null): TierRef[] {
  const present = [held, target].filter((t): t is TierRef => t !== null);
  return [...new Map(present.map((t) => [t.value, t])).values()].sort((a, b) => a.value - b.value);
}

export function tierLabel(t: TierRef): string {
  return `${t.tier} · ${t.value}%`;
}

/** Tier-held label with the below-entry case spelled out rather than blank. */
export function tierHeldLabel(t: TierRef | null): string {
  return t ? tierLabel(t) : `Below ${TIERS[0]!.tier}`;
}

/**
 * Three-state triage, driven by the bands above: only the 75–79% band waits on
 * a human, only ≥80% is left alone, everything else is auto-nudged. The status
 * dot mirrors the comms action — red = agent nudged, amber = your approval is
 * pending, green = deliberately no comms.
 */
export function triage(share: number): {
  status: ShareStatus;
  comms: "autosent" | "approve" | "notsent";
} {
  switch (classifyBand(share)) {
    case "silent":
      return { status: "ok", comms: "notsent" };
    case "top_hold":
      return { status: "warn", comms: "approve" };
    default:
      return { status: "risk", comms: "autosent" };
  }
}

/** Quarters covered by a chart range, ending at the current quarter. */
export function rangeQuarters(range: ShareRange): string[] {
  // Literal counts: "3" = exactly the last 3 quarters incl. the current one.
  // (The mockup returned n+1; the labels promised n — labels win.)
  const i = QUARTERS.indexOf(CURRENT_QUARTER);
  const n =
    { "1": 1, "2": 2, "3": 3, "4": 4, "1Y": 4, "5Y": QUARTERS.length, MAX: QUARTERS.length }[
      range
    ] ?? 3;
  return QUARTERS.slice(Math.max(0, i - n + 1), i + 1);
}

// ── Email templates (ported from the mockup) ─────────────────────────────

const EMAIL_BODY: Record<EventType, string> = {
  order_received:
    "Dear {cust} team, your order {n} has been received and is currently being processed. Our fulfilment team is preparing it now — more updates to follow as it progresses.",
  shipment_created:
    "Dear {cust} team, a shipment has been created for your order {n} and is being prepared to leave our facility. We'll let you know the moment it's on the move.",
  fulfilment_status:
    "Dear {cust} team, our warehouse is currently picking and packing your order {n}. Everything is on track and moving to dispatch shortly.",
  shipped:
    "Good news, {cust} team — your order {n} has shipped and is on its way. Your tracking number is {track}, so you can follow its journey to your door.",
  passed_to_carrier:
    "Dear {cust} team, your order {n} has now been handed to the carrier and is in transit for delivery.",
  delivered:
    "Dear {cust} team, your order {n} has been delivered. Thank you for choosing J&J Vision — we hope everything arrived in great condition.",
  backorder:
    "Dear {cust} team, one or more items in your order {n} are currently on backorder. Based on our latest supply information the affected items are now estimated to ship by {revised} — an estimate rather than a confirmed date. We'll send your next update by {next}, and your dedicated account manager is following up in the meantime.",
};

const DATES = [
  "2026-07-07",
  "2026-07-08",
  "2026-07-09",
  "2026-07-10",
  "2026-07-11",
  "2026-07-12",
  "2026-07-13",
];

function sentAt(stepIndex: number): string {
  // Stagger seeded send times through the morning so the log doesn't read as
  // a batch blast; live-triggered events use the real clock instead.
  const hour = String(8 + (stepIndex % 4)).padStart(2, "0");
  const minute = ["05", "20", "35", "50"][stepIndex % 4];
  return `${DATES[Math.min(stepIndex, DATES.length - 1)]}T${hour}:${minute}:00Z`;
}

/** Days from the backorder notice to the revised ETA and to the next update. */
const REVISED_ETA_DAYS = 14;
const NEXT_UPDATE_DAYS = 5;

/** "July 22, 2026" — long form so the email body reads like prose, not a log line. */
function longDate(iso: string, plusDays: number): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + plusDays);
  return d.toLocaleDateString("en-US", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function fillTemplate(
  template: string,
  customerName: string,
  orderNumber: number,
  sentAt: string,
): string {
  return (
    template
      .replace(/\{cust\}/g, customerName)
      .replace(/\{n\}/g, `#${orderNumber}`)
      // Carrier reference is a deterministic function of the order number — the
      // demo has no carrier integration to get a real one from.
      .replace(/\{track\}/g, `1Z-JJV-${orderNumber}`)
      .replace(/\{revised\}/g, longDate(sentAt, REVISED_ETA_DAYS))
      .replace(/\{next\}/g, longDate(sentAt, NEXT_UPDATE_DAYS))
  );
}

export interface EmailRecordOptions {
  eventType: EventType;
  sentAt: string;
  customer: Pick<StoreCustomer, "name" | "contact" | "rep">;
  orderNumber: number;
  suppressed: boolean;
  suppressionReason?: string;
}

/** Backorder emails CC the account rep — the mockup's "rep follows up" flow. */
export function makeEmailRecord(opts: EmailRecordOptions): EmailRecord {
  const { eventType, customer, orderNumber, suppressed, suppressionReason } = opts;
  return {
    event_type: eventType,
    sent_at: opts.sentAt,
    to: `${customer.contact.who} <${customer.contact.email}>`,
    from: SENDER,
    ...(eventType === "backorder" ? { cc: `${customer.rep.name} <${customer.rep.email}>` } : {}),
    body: fillTemplate(EMAIL_BODY[eventType], customer.name, orderNumber, opts.sentAt),
    status: suppressed ? "suppressed" : "sent",
    ...(suppressed ? { suppression_reason: suppressionReason ?? OPT_OUT_REASON } : {}),
    // This whole in-memory world is synthetic demo data.
    provenance: "mock",
  };
}

// ── World shape ──────────────────────────────────────────────────────────

export interface StoreOrder {
  id: string;
  number: number;
  reached: number;
  backordered: boolean;
  emails: EmailRecord[];
}

export interface StoreCustomer {
  id: string;
  name: string;
  attention: AttentionLevel;
  ordersInProgress: number;
  emailsSent: number;
  contact: { who: string; email: string };
  rep: { name: string; email: string };
  prefs: NotificationPreferences;
  /** Key/EDI account: every event is suppressed regardless of `prefs`. */
  excluded: boolean;
  orders: StoreOrder[];
}

export interface QueueEntry {
  customerId: string;
  orderId: string;
  kind: "step" | "backorder";
}

export interface QuarterSnap {
  before: number;
  final?: number;
  status?: ShareStatus;
  comms: CommsState;
}

export interface StoreAccount {
  id: string;
  name: string;
  rep: string;
  about: string;
  mix: ShareMix;
  series: Record<string, number>;
  byQuarter: Record<string, QuarterSnap>;
  pending: boolean;
  draftSentAt?: string;
  draftSkippedAt?: string;
}

export interface World {
  customers: StoreCustomer[];
  queue: QueueEntry[];
  accounts: StoreAccount[];
}

// ── Derived KPIs ─────────────────────────────────────────────────────────

// Nudge counts and share movement are read off the live per-quarter comms
// history rather than stored as totals, so approving a draft moves the KPI
// tiles the same instant it moves the row — and the list and the account page
// can't drift apart.

/** Quarters where an email actually went out (auto-nudge or approved nudge). */
function nudgedQuarters(account: StoreAccount): QuarterSnap[] {
  return Object.values(account.byQuarter).filter(
    (s) => s.comms === "sent" || s.comms === "autosent",
  );
}

/** Share movement per nudged quarter — closed quarters only (they have a final). */
function shareDiffs(snaps: QuarterSnap[]): number[] {
  return snaps.filter((s) => s.final !== undefined).map((s) => s.final! - s.before);
}

function meanTo1dp(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
}

export function accountNudgeStats(account: StoreAccount): {
  nudges_sent: number;
  avg_share_diff_after_nudge: number;
} {
  const nudged = nudgedQuarters(account);
  return {
    nudges_sent: nudged.length,
    avg_share_diff_after_nudge: meanTo1dp(shareDiffs(nudged)),
  };
}

/** Org-wide KPI tiles on the share list screen. */
export function shareKpis(accounts: StoreAccount[]): ShareSummaryKpis {
  const nudged = accounts.flatMap(nudgedQuarters);
  return {
    at_or_above_top_tier: accounts.filter((a) => a.series[CURRENT_QUARTER]! >= TOP_TIER.value)
      .length,
    total_nudges_sent: nudged.length,
    avg_share_diff_after_nudge: meanTo1dp(shareDiffs(nudged)),
  };
}

// ── Ordering seeds ───────────────────────────────────────────────────────

const REPS = [
  { name: "Danielle Reyes", email: "danielle.reyes@jnjvision.com" },
  { name: "Aiden Clarke", email: "aiden.clarke@jnjvision.com" },
  { name: "Marcus Bell", email: "marcus.bell@jnjvision.com" },
  { name: "Priya Nair", email: "priya.nair@jnjvision.com" },
];

const DOT_TO_ATTENTION: Record<"ok" | "warn" | "risk", AttentionLevel> = {
  ok: "ok",
  warn: "progress",
  risk: "risk",
};

interface CustomerSeed {
  name: string;
  dot: "ok" | "warn" | "risk";
  inProgress: number;
  /** Mockup hardcodes this for its six customers; computed when absent. */
  emails?: number;
  contact: { who: string; email: string };
  repIndex: number;
  /** Aligned with EVENT_TYPES order (7 booleans). All false ⇒ opted out. */
  prefs: readonly boolean[];
  /** Key/EDI account — suppressed whatever `prefs` says. */
  excluded?: boolean;
  orders?: readonly { number: number; reached: number; backordered: boolean }[];
}

const ALL_ON = [true, true, true, true, true, true, true] as const;
const ALL_OFF = [false, false, false, false, false, false, false] as const;

// First six ported verbatim from the mockup; the rest extend the world to
// ~15 customers using the mockup's own `genOrders` generation.
const CUSTOMER_SEEDS: readonly CustomerSeed[] = [
  {
    name: "Bright Eyes Optometry",
    dot: "risk",
    inProgress: 23,
    emails: 12,
    contact: { who: "Alex Monroe, Practice Manager", email: "orders@brighteyesoptometry.com" },
    repIndex: 0,
    prefs: [false, true, false, true, false, true, false],
    orders: [
      { number: 34998225, reached: 2, backordered: false },
      { number: 34998223, reached: 4, backordered: false },
      { number: 34998226, reached: 1, backordered: false },
      { number: 34998227, reached: 6, backordered: false },
      { number: 34998229, reached: 1, backordered: false },
      { number: 34998230, reached: 1, backordered: true },
    ],
  },
  {
    name: "Clearview Vision Center",
    dot: "warn",
    inProgress: 10,
    emails: 6,
    contact: { who: "Jordan Whitfield, Office Lead", email: "orders@clearviewvision.com" },
    repIndex: 2,
    prefs: [true, true, false, true, false, true, true],
  },
  {
    name: "Summit Eye Care",
    dot: "warn",
    inProgress: 3,
    emails: 0,
    contact: { who: "Practice Manager", email: "admin@summiteyecare.com" },
    repIndex: 3,
    prefs: ALL_OFF,
  },
  {
    name: "Harbor Optical Group",
    dot: "warn",
    inProgress: 7,
    emails: 0,
    contact: { who: "Practice Manager", email: "admin@harboroptical.com" },
    repIndex: 0,
    prefs: ALL_OFF,
  },
  {
    name: "Lakeside Family Optometry",
    dot: "ok",
    inProgress: 13,
    emails: 9,
    contact: { who: "Sam Okafor, Practice Manager", email: "orders@lakesidefamilyeye.com" },
    repIndex: 2,
    prefs: ALL_ON,
  },
  {
    name: "Northgate Vision Clinic",
    dot: "warn",
    inProgress: 5,
    emails: 4,
    contact: { who: "Riley Chen, Front Desk Lead", email: "orders@northgatevision.com" },
    repIndex: 3,
    prefs: [false, true, false, true, false, true, false],
  },
  {
    name: "Cascade Eye Associates",
    dot: "ok",
    inProgress: 8,
    contact: { who: "Practice Manager", email: "orders@cascadeeye.com" },
    repIndex: 3,
    prefs: ALL_ON,
  },
  {
    name: "Riverbend Optical",
    dot: "warn",
    inProgress: 4,
    contact: { who: "Practice Manager", email: "orders@riverbendoptical.com" },
    repIndex: 0,
    prefs: [true, true, false, true, false, true, true],
  },
  {
    name: "Golden Gate Eye Care",
    dot: "ok",
    inProgress: 11,
    contact: { who: "Practice Manager", email: "orders@goldengateeye.com" },
    repIndex: 1,
    prefs: ALL_ON,
  },
  {
    name: "Maple Grove Vision",
    dot: "warn",
    inProgress: 6,
    contact: { who: "Practice Manager", email: "orders@maplegrovevision.com" },
    repIndex: 2,
    prefs: ALL_ON,
  },
  {
    name: "Pinnacle Eye Institute",
    dot: "risk",
    inProgress: 9,
    contact: { who: "Practice Manager", email: "orders@pinnacleeye.com" },
    repIndex: 1,
    prefs: ALL_ON,
  },
  {
    name: "Willow Creek Optometry",
    dot: "ok",
    inProgress: 2,
    contact: { who: "Practice Manager", email: "orders@willowcreekoptometry.com" },
    repIndex: 3,
    prefs: ALL_ON,
  },
  {
    name: "Bayview Optical Studio",
    dot: "warn",
    inProgress: 5,
    contact: { who: "Practice Manager", email: "orders@bayviewoptical.com" },
    repIndex: 0,
    prefs: ALL_ON,
  },
  {
    name: "Stonebridge Eye Clinic",
    dot: "warn",
    inProgress: 3,
    contact: { who: "Practice Manager", email: "admin@stonebridgeeye.com" },
    repIndex: 1,
    prefs: ALL_OFF,
  },
  {
    name: "Aurora Vision Partners",
    dot: "ok",
    inProgress: 7,
    contact: { who: "Practice Manager", email: "orders@auroravision.com" },
    repIndex: 2,
    prefs: ALL_ON,
  },
  // Guardrail case: a large multi-site distributor ordering over EDI. Its
  // orders progress normally but every event is suppressed and logged —
  // exclusion outranks its (fully opted-in) preferences.
  {
    name: "Nordvale Optical Distribution — EDI",
    dot: "warn",
    inProgress: 48,
    contact: { who: "Procurement Desk", email: "edi-orders@nordvaledistribution.com" },
    repIndex: 1,
    prefs: ALL_ON,
    excluded: true,
    orders: [
      { number: 34998281, reached: 3, backordered: false },
      { number: 34998283, reached: 5, backordered: false },
      { number: 34998285, reached: 2, backordered: true },
    ],
  },
];

/**
 * Mockup `genOrders` — deterministic per-customer order list. The base is
 * spaced 20 apart and kept clear of the hardcoded seed numbers above, so no
 * two customers ever show the same order number (it's unique in the schema).
 */
function genOrders(
  seed: number,
  count: number,
): { number: number; reached: number; backordered: boolean }[] {
  const base = 34998300 + seed * 20;
  const reaches = [2, 4, 1, 6, 1, 3];
  return Array.from({ length: Math.min(count, 6) }, (_, i) => ({
    number: base + i * 2,
    reached: reaches[i % reaches.length]!,
    backordered: i === 2,
  }));
}

function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Mockup `orderEmails` + `emailCount`: pre-existing events generated one
 * email per reached step (+ backorder). Exclusion, then whole-customer
 * opt-out, suppresses; per-event preferences only gate *future* (queued)
 * events, as in the mockup.
 */
function buildOrderEmails(
  order: { number: number; reached: number; backordered: boolean },
  customer: Pick<StoreCustomer, "name" | "contact" | "rep" | "excluded">,
  optedIn: boolean,
): EmailRecord[] {
  const suppressed = customer.excluded || !optedIn;
  const suppressionReason = customer.excluded ? EDI_EXCLUDED_REASON : undefined;
  const records: EmailRecord[] = [];
  for (let i = 0; i < order.reached; i++) {
    records.push(
      makeEmailRecord({
        eventType: STEP_EVENT_TYPES[i]!,
        sentAt: sentAt(i),
        customer,
        orderNumber: order.number,
        suppressed,
        suppressionReason,
      }),
    );
  }
  if (order.backordered) {
    records.push(
      makeEmailRecord({
        eventType: "backorder",
        sentAt: sentAt(order.reached),
        customer,
        orderNumber: order.number,
        suppressed,
        suppressionReason,
      }),
    );
  }
  return records;
}

// Queued, not-yet-processed order events for the demo trigger
// (`POST /api/admin/demo/next-event`), oldest first. Picked for variety:
// suppressed-by-preference, suppressed-by-opt-out, plain emails, and a
// backorder email with the rep on CC.
// Orders are referenced by their index in the customer's list, not by number,
// so renumbering the seeds above can't silently point the queue at nothing.
const QUEUE_SEED: readonly { cust: number; orderIdx: number; kind: "step" | "backorder" }[] = [
  { cust: 0, orderIdx: 0, kind: "step" }, // Bright Eyes → fulfilment_status, pref off ⇒ suppressed
  { cust: 4, orderIdx: 0, kind: "step" }, // Lakeside → fulfilment_status ⇒ emailed
  { cust: 2, orderIdx: 0, kind: "step" }, // Summit (opted out) ⇒ suppressed
  { cust: 1, orderIdx: 0, kind: "backorder" }, // Clearview ⇒ backorder email, rep CC'd
  { cust: 5, orderIdx: 1, kind: "step" }, // Northgate → passed_to_carrier, pref off ⇒ suppressed
  { cust: 5, orderIdx: 1, kind: "step" }, // Northgate → delivered ⇒ emailed
  { cust: 0, orderIdx: 0, kind: "step" }, // Bright Eyes → shipped ⇒ emailed
];

// ── Share seeds ──────────────────────────────────────────────────────────

interface AccountSeed {
  name: string;
  rep: string;
  about: string;
  mix: ShareMix;
  series: Record<string, number>;
  /** Hand-tuned closed quarters from the mockup; generated when absent. */
  closed?: Record<string, { before: number; final: number }>;
}

/** Deterministic series ramp ending exactly at `end` in the current quarter. */
function genSeries(end: number, slope: number): Record<string, number> {
  const wiggle = [0, 1, -1, 0, 1, 0, -1, 1, 0, -1, 0];
  const series: Record<string, number> = {};
  QUARTERS.forEach((q, i) => {
    series[q] = Math.round(end - slope * (QUARTERS.length - 1 - i)) + wiggle[i]!;
  });
  return series;
}

// First eight ported verbatim from the mockup (series + hand-tuned closed
// quarters); the rest extend the world to 15 preferred accounts. The current
// quarter's status/comms is always derived from `triage()` — it reproduces
// the mockup's hardcoded 2026Q3 snapshots exactly.
const ACCOUNT_SEEDS: readonly AccountSeed[] = [
  {
    name: "Bright Eyes Optometry",
    rep: "Danielle Reyes",
    about:
      "High-volume independent practice, three locations across the metro. Long-standing preferred account, sitting exactly on the 80% line where the programme stops nudging.",
    mix: { jj: 80, alcon: 10, cooper: 6, other: 4 },
    series: {
      "2024Q1": 68,
      "2024Q2": 69,
      "2024Q3": 70,
      "2024Q4": 71,
      "2025Q1": 70,
      "2025Q2": 72,
      "2025Q3": 74,
      "2025Q4": 76,
      "2026Q1": 72,
      "2026Q2": 78,
      "2026Q3": 80,
    },
    closed: {
      "2026Q2": { before: 78, final: 80 },
      "2026Q1": { before: 70, final: 72 },
      "2025Q4": { before: 74, final: 76 },
    },
  },
  {
    name: "Precision Eye Partners",
    rep: "Aiden Clarke",
    about:
      "Specialty practice with a premium lens focus and a loyal patient base. Holds T3 with 4 points to spare — inside the 75–79% approval window, so its reminder waits on an operator.",
    mix: { jj: 79, alcon: 13, cooper: 5, other: 3 },
    series: {
      "2024Q1": 70,
      "2024Q2": 71,
      "2024Q3": 72,
      "2024Q4": 73,
      "2025Q1": 74,
      "2025Q2": 74,
      "2025Q3": 75,
      "2025Q4": 76,
      "2026Q1": 76,
      "2026Q2": 78,
      "2026Q3": 79,
    },
    closed: {
      "2026Q2": { before: 78, final: 79 },
      "2026Q1": { before: 76, final: 78 },
      "2025Q4": { before: 75, final: 76 },
    },
  },
  {
    name: "Clearview Vision Center",
    rep: "Marcus Bell",
    about:
      "Growing suburban group with a strong myopia-management programme. Now one of the stronger preferred accounts, just inside the T3 approval window.",
    mix: { jj: 78, alcon: 13, cooper: 6, other: 3 },
    series: {
      "2024Q1": 60,
      "2024Q2": 62,
      "2024Q3": 64,
      "2024Q4": 66,
      "2025Q1": 68,
      "2025Q2": 70,
      "2025Q3": 72,
      "2025Q4": 74,
      "2026Q1": 73,
      "2026Q2": 75,
      "2026Q3": 78,
    },
    closed: {
      "2026Q2": { before: 75, final: 78 },
      "2026Q1": { before: 73, final: 75 },
      "2025Q4": { before: 74, final: 74 },
    },
  },
  {
    name: "Lakeside Family Optometry",
    rep: "Marcus Bell",
    about:
      "Family practice with steady growth and a strong daily-disposable mix. Holds T2 and is closing on T3, so the agent nudges it on both tiers.",
    mix: { jj: 56, alcon: 26, cooper: 11, other: 7 },
    series: {
      "2024Q1": 45,
      "2024Q2": 46,
      "2024Q3": 47,
      "2024Q4": 48,
      "2025Q1": 49,
      "2025Q2": 50,
      "2025Q3": 51,
      "2025Q4": 52,
      "2026Q1": 53,
      "2026Q2": 54,
      "2026Q3": 56,
    },
    closed: {
      "2026Q2": { before: 54, final: 56 },
      "2026Q1": { before: 52, final: 54 },
      "2025Q4": { before: 51, final: 52 },
    },
  },
  {
    name: "Harbor Optical Group",
    rep: "Danielle Reyes",
    about:
      "Coastal multi-site group with a loyal patient base. Three points short of T3 this quarter — a prime candidate for a well-timed nudge.",
    mix: { jj: 72, alcon: 16, cooper: 8, other: 4 },
    series: {
      "2024Q1": 64,
      "2024Q2": 65,
      "2024Q3": 66,
      "2024Q4": 67,
      "2025Q1": 68,
      "2025Q2": 69,
      "2025Q3": 70,
      "2025Q4": 71,
      "2026Q1": 70,
      "2026Q2": 73,
      "2026Q3": 72,
    },
    closed: {
      "2026Q2": { before: 73, final: 72 },
      "2026Q1": { before: 70, final: 73 },
      "2025Q4": { before: 71, final: 71 },
    },
  },
  {
    name: "Summit Eye Care",
    rep: "Priya Nair",
    about:
      "Mid-size practice rebalancing its lens portfolio after a competitor promotion. Holds T1, three points short of T2, and moves quarter to quarter.",
    mix: { jj: 47, alcon: 30, cooper: 15, other: 8 },
    series: {
      "2024Q1": 44,
      "2024Q2": 45,
      "2024Q3": 46,
      "2024Q4": 48,
      "2025Q1": 47,
      "2025Q2": 49,
      "2025Q3": 48,
      "2025Q4": 50,
      "2026Q1": 47,
      "2026Q2": 49,
      "2026Q3": 47,
    },
    closed: {
      "2026Q2": { before: 49, final: 47 },
      "2026Q1": { before: 47, final: 50 },
      "2025Q4": { before: 48, final: 50 },
    },
  },
  {
    name: "Northgate Vision Clinic",
    rep: "Priya Nair",
    about:
      "Urban clinic with high patient turnover. Fell back to T1 after a staffing change disrupted reordering.",
    mix: { jj: 44, alcon: 32, cooper: 16, other: 8 },
    series: {
      "2024Q1": 50,
      "2024Q2": 49,
      "2024Q3": 48,
      "2024Q4": 47,
      "2025Q1": 46,
      "2025Q2": 45,
      "2025Q3": 44,
      "2025Q4": 43,
      "2026Q1": 40,
      "2026Q2": 42,
      "2026Q3": 44,
    },
    closed: {
      "2026Q2": { before: 42, final: 44 },
      "2026Q1": { before: 40, final: 44 },
      "2025Q4": { before: 43, final: 43 },
    },
  },
  {
    name: "Meridian Eyecare Associates",
    rep: "Aiden Clarke",
    about:
      "Recently acquired by a regional chain; ordering patterns shifting toward a competitor. Now below the 30% T1 entry tier and flagged for review.",
    mix: { jj: 26, alcon: 40, cooper: 22, other: 12 },
    series: {
      "2024Q1": 38,
      "2024Q2": 37,
      "2024Q3": 35,
      "2024Q4": 34,
      "2025Q1": 33,
      "2025Q2": 32,
      "2025Q3": 31,
      "2025Q4": 30,
      "2026Q1": 32,
      "2026Q2": 28,
      "2026Q3": 26,
    },
    closed: {
      "2026Q2": { before: 28, final: 26 },
      "2026Q1": { before: 32, final: 30 },
      "2025Q4": { before: 31, final: 30 },
    },
  },
  // ── Extended accounts (deterministic ramps, triaged like the rest) ──
  {
    name: "Cascade Eye Associates",
    rep: "Priya Nair",
    about:
      "Regional group with a strong specialty-fit practice. Well clear of T3 and steadily growing — deliberately left alone.",
    mix: { jj: 84, alcon: 8, cooper: 5, other: 3 },
    series: genSeries(84, 0.8),
  },
  {
    name: "Riverbend Optical",
    rep: "Danielle Reyes",
    about:
      "Independent practice recovering from a slow spring. Just over the T2 threshold, still 22 points from T3.",
    mix: { jj: 53, alcon: 28, cooper: 12, other: 7 },
    series: genSeries(53, 0.5),
  },
  {
    name: "Golden Gate Eye Care",
    rep: "Aiden Clarke",
    about:
      "Boutique urban practice with a competitor-heavy mix. Hovers just above the T1 entry tier quarter to quarter.",
    mix: { jj: 33, alcon: 38, cooper: 19, other: 10 },
    series: genSeries(33, 0.3),
  },
  {
    name: "Maple Grove Vision",
    rep: "Marcus Bell",
    about:
      "Suburban family practice with reliable reordering habits. Holds T2 comfortably and drifts toward T3.",
    mix: { jj: 62, alcon: 22, cooper: 10, other: 6 },
    series: genSeries(62, 0.7),
  },
  {
    name: "Pinnacle Eye Institute",
    rep: "Aiden Clarke",
    about:
      "Surgical-referral practice whose lens orders dipped after a supplier trial. Now sits mid-band between T2 and T3.",
    mix: { jj: 68, alcon: 18, cooper: 9, other: 5 },
    series: genSeries(68, -0.4),
  },
  {
    name: "Willow Creek Optometry",
    rep: "Priya Nair",
    about:
      "Small-town practice trending away from the portfolio. Fell below the T1 entry tier and was nudged automatically.",
    mix: { jj: 29, alcon: 42, cooper: 19, other: 10 },
    series: genSeries(29, -0.5),
  },
  {
    name: "Aurora Vision Partners",
    rep: "Marcus Bell",
    about:
      "Two-location partnership with a growing daily-disposable base. Holds T2 with T3 still some way off.",
    mix: { jj: 58, alcon: 24, cooper: 11, other: 7 },
    series: genSeries(58, 0.6),
  },
  // ── Band-boundary cases, so every edge of the client rule is walkable in
  // the demo: 74 (auto, one point short of the approval window), 75 (approval
  // window opens exactly on the threshold), 50 and 30 (auto on an exact tier
  // threshold — the share == threshold edge). 80 (silence opens) is Bright
  // Eyes, already in the world.
  {
    name: "Ridgeway Eye Clinic",
    rep: "Danielle Reyes",
    about:
      "Busy suburban clinic that ends most quarters within touching distance of T3. At 74% it is one point short — the case the client asked about by name, and still an automatic nudge.",
    mix: { jj: 74, alcon: 15, cooper: 7, other: 4 },
    series: genSeries(74, 0.6),
  },
  {
    name: "Cedar Point Eyecare",
    rep: "Aiden Clarke",
    about:
      "Established group practice sitting exactly on the 75% T3 threshold — the first share that needs an operator's approval rather than an automatic send.",
    mix: { jj: 75, alcon: 14, cooper: 7, other: 4 },
    series: genSeries(75, 0.6),
  },
  {
    name: "Fairview Optical Partners",
    rep: "Marcus Bell",
    about:
      "Two-site partnership landing exactly on the 50% T2 threshold: it holds T2 (a threshold counts as held) and is auto-nudged about T3.",
    mix: { jj: 50, alcon: 28, cooper: 14, other: 8 },
    series: genSeries(50, 0.5),
  },
  {
    name: "Brookline Vision Care",
    rep: "Priya Nair",
    about:
      "Small practice that has just reached the 30% T1 entry tier exactly — inside the programme, auto-nudged about T1 and T2.",
    mix: { jj: 30, alcon: 40, cooper: 20, other: 10 },
    series: genSeries(30, 0.4),
  },
];

const CLOSED_SNAP_QUARTERS = ["2025Q4", "2026Q1", "2026Q2"];

function genClosedSnaps(
  series: Record<string, number>,
): Record<string, { before: number; final: number }> {
  const snaps: Record<string, { before: number; final: number }> = {};
  for (const q of CLOSED_SNAP_QUARTERS) {
    const prev = QUARTERS[QUARTERS.indexOf(q) - 1]!;
    snaps[q] = { before: series[prev]!, final: series[q]! };
  }
  return snaps;
}

// ── Draft email (mockup `draftEmail`) ────────────────────────────────────

export const DRAFT_DATE = "2026-07-07";

/**
 * Three variants, one per band that produces a nudge (client rule, Martin
 * 2026-07-29 — "the email reflecting how close they are to the relevant
 * tiers"):
 *
 * - `below_entry` → how close they are to T1, the entry tier;
 * - `between`     → both relevant tiers: the one they hold and the next one up;
 * - `top_hold`    → position above T3, maintain it (this is the one an operator
 *   approves by hand).
 *
 * `silent` accounts never receive a nudge; they reuse the top-hold copy so the
 * drawer can still show what would have been said.
 *
 * All three stay awareness-only: no order quantities, no stock levels, no
 * commitment on either side.
 */
export function buildDraft(account: StoreAccount): { subject: string; body: string } {
  const share = account.series[CURRENT_QUARTER]!;
  const subject = "J&J Ordering Share Status";
  const closing = `Thank you for your continued partnership.\n\nThe J&J Vision team`;
  const repLine = `Your dedicated rep is on hand if you'd like to review your product mix or upcoming reorders.`;
  const open = `Dear ${account.name} team,\n\n`;
  const band = classifyBand(share);

  if (band === "below_entry") {
    const entry = TIERS[0]!;
    return {
      subject,
      body:
        open +
        `Your J&J order share currently sits at ${share}%. The preferred programme's entry tier, ${entry.tier}, starts at ${entry.value}% — ${entry.value - share} percentage points away.\n\n` +
        `An increase in your J&J Vision ordering this quarter would bring ${entry.tier} benefits into reach.\n\n` +
        `${repLine}\n\n` +
        closing,
    };
  }

  if (band === "between") {
    // Both relevant tiers in one message: the held one and the next one up.
    const held = tierHeld(share)!;
    const next = nextTier(share)!;
    const heldPhrase =
      share === held.value
        ? `That puts you exactly on the ${held.value}% ${held.tier} threshold`
        : `That keeps you ${share - held.value} percentage points clear of the ${held.value}% ${held.tier} threshold`;
    return {
      subject,
      body:
        open +
        `Your J&J order share currently sits at ${share}%. ${heldPhrase}, and leaves you ${next.value - share} points short of the ${next.value}% ${next.tier} threshold.\n\n` +
        `Holding your current level keeps your ${held.tier} benefits; a further increase this quarter would bring ${next.tier} within reach.\n\n` +
        `${repLine}\n\n` +
        closing,
    };
  }

  const over = share - TOP_TIER.value;
  const position =
    over === 0
      ? `exactly on the ${TOP_TIER.value}% ${TOP_TIER.tier} threshold, the programme's top tier`
      : `${over} percentage points above the ${TOP_TIER.value}% ${TOP_TIER.tier} threshold, the programme's top tier`;
  return {
    subject,
    body:
      open +
      `Your J&J order share currently sits at ${share}% — ${position}.\n\n` +
      `No change is needed to keep your ${TOP_TIER.tier} benefits next quarter: maintaining your current ordering level is enough.\n\n` +
      `${repLine}\n\n` +
      closing,
  };
}

// ── World construction ───────────────────────────────────────────────────

function buildWorld(): World {
  const customers: StoreCustomer[] = CUSTOMER_SEEDS.map((seed, idx) => {
    const id = slug(seed.name);
    const rep = REPS[seed.repIndex]!;
    const prefs = Object.fromEntries(
      EVENT_TYPES.map((eventType, i) => [eventType, seed.prefs[i]]),
    ) as NotificationPreferences;
    const optedIn = seed.prefs.some(Boolean);
    // Copy the seed objects — the world is mutable and `reset()` must hand back
    // a pristine one, so nothing may alias the module-level seeds.
    const identity = {
      name: seed.name,
      contact: { ...seed.contact },
      rep: { ...rep },
      excluded: seed.excluded ?? false,
    };
    const orders: StoreOrder[] = (seed.orders ?? genOrders(idx, seed.inProgress)).map((o) => ({
      id: `${id}-${o.number}`,
      number: o.number,
      reached: o.reached,
      backordered: o.backordered,
      emails: buildOrderEmails(o, identity, optedIn),
    }));
    const emailsSent =
      seed.emails ??
      orders.reduce((n, o) => n + o.emails.filter((e) => e.status === "sent").length, 0);
    return {
      id,
      name: seed.name,
      attention: DOT_TO_ATTENTION[seed.dot],
      ordersInProgress: seed.inProgress,
      emailsSent,
      contact: identity.contact,
      rep: identity.rep,
      prefs,
      excluded: identity.excluded,
      orders,
    };
  });

  const queue: QueueEntry[] = QUEUE_SEED.map((e) => ({
    customerId: customers[e.cust]!.id,
    orderId: customers[e.cust]!.orders[e.orderIdx]!.id,
    kind: e.kind,
  }));

  const accounts: StoreAccount[] = ACCOUNT_SEEDS.map((seed) => {
    const byQuarter: Record<string, QuarterSnap> = {};
    for (const [q, snap] of Object.entries(seed.closed ?? genClosedSnaps(seed.series))) {
      byQuarter[q] = { before: snap.before, final: snap.final, comms: "sent" };
    }
    const currentShare = seed.series[CURRENT_QUARTER]!;
    const t = triage(currentShare);
    byQuarter[CURRENT_QUARTER] = { before: currentShare, status: t.status, comms: t.comms };
    return {
      id: slug(seed.name),
      name: seed.name,
      rep: seed.rep,
      about: seed.about,
      mix: { ...seed.mix },
      series: { ...seed.series },
      byQuarter,
      pending: t.comms === "approve",
    };
  });

  return { customers, queue, accounts };
}

/**
 * The live world — no longer read by `api.ts` (that calls the real backend
 * now), but kept mutable and reset-able for anything that still wants an
 * in-memory demo world. `store.reset()` restores the pristine deterministic
 * state.
 */
export const store: { world: World; reset: () => void } = {
  world: buildWorld(),
  reset: () => {
    store.world = buildWorld();
  },
};
